import clsx from 'clsx'
import { Spinner } from 'components/Spinner/Spinner'
import { useFormatPrice } from 'hooks/useFormatPrice'
import { usePIMData } from 'hooks/usePIMData'
import { NextSeo } from 'next-seo'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import invariant from 'tiny-invariant'
import { PageType, ProductVariant } from 'typings/api'
import { MediaGallery } from './components/MediaGallery/MediaGallery'
import { selectMatchingItem } from './lib/selectMatchingItem'
import { DevTool } from '@hookform/devtools'
import { Subscription } from 'react-hook-form/dist/utils/createSubject'
import { renderModules } from './lib/renderModules'
import { selectContentModule } from 'lib/selectContentModule'
import { Breadcrumb } from 'elements/Breadcrumb/Breadcrumb'

export type Props = { urlKey: string }
export type FormValues = { [key: string]: string }

export const ProductDetailPage = ({ urlKey }: Props) => {
  const { register, handleSubmit, control, watch } = useForm<FormValues>()
  const { data, isLoading, isError, error } = usePIMData<PageType>(urlKey, {
    onSuccess: (data) => {
      try {
        invariant(data.type === 'pdp')
        /**
         * If items.length === 1 we don't need to display any options
         * selection
         */
        if (data.main.items.length === 1) {
          setSelectedItem(data.main.items[0])
        }
      } catch (e) {
        console.error(e)
      }
    },
    search: false,
  })
  const [selectedItem, setSelectedItem] = useState<ProductVariant>(null)
  const onSubmit = (values: FormValues) => {
    try {
      invariant(data.type === 'pdp', 'Wrong data type supplied in data')
      // check if we have as many values selected as there are option values
      if (Object.values(values).filter((val) => !!val)?.length === data.main.options.length) {
        setSelectedItem(selectMatchingItem({ values, items: data.main.items }))
      }
    } catch (e) {
      console.error(e.message)
    }
  }

  useEffect(() => {
    let subscription: Subscription
    try {
      invariant(data?.type === 'pdp', 'Wrong data type supplied in data')
      subscription = watch((value) => {
        console.dir(value)
        // check if we have as many values selected as there are option values
        if (Object.values(value).filter((val) => !!val)?.length === data.main.options.length) {
          setSelectedItem(selectMatchingItem({ values: value, items: data.main.items }))
        }
      })
    } catch (e) {
      console.error(e.message)
    }
    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [data, watch])

  if (isLoading) {
    return <Spinner fullsize />
  }

  if (isError && process.env.NODE_ENV === 'development') {
    return <div>{error.message}</div>
  } else if (isError) {
    return <div>Something went wrong</div>
  }

  invariant(data.type === 'pdp', 'Wrong data type supplied in data')

  const Options = ({ title, values, uid }: typeof data.main.options[number]) => {
    return (
      <div className="mb-4">
        <p className="mb-2 text-xs font-bold text-gray-dark md:mb-2 md:text-base">{title}</p>
        <ul className="grid grid-cols-2 items-center justify-center">
          {values.map((optionVal, i) => {
            const uuid = `${title}.${optionVal.uid}.${i}`

            return (
              <li key={uuid} className="w-ftablel">
                <input
                  {...register(uid)}
                  type="radio"
                  id={uuid}
                  value={optionVal.uid}
                  className="peer sr-only"
                />
                <label
                  htmlFor={uuid}
                  className={
                    'w-ftablel block cursor-pointer border border-gray py-2 text-center text-base transition-colors peer-checked:border-red peer-checked:bg-red peer-checked:text-white'
                  }
                >
                  {optionVal.label}
                </label>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <div className="md:mb-10">
      <div className="mx-auto max-w-screen-xl md:mb-10">
        {data.breadcrumb && data.breadcrumb.length >= 1 && (
          <Breadcrumb breadcrumbData={data.breadcrumb} pageTitle={data?.title} />
        )}
        <div className="mb-10 grid-cols-2 gap-x-9 gap-y-10 md:grid">
          <NextSeo
            title={data.title}
            description={data.meta_description}
            additionalMetaTags={[{ name: 'title', content: data.meta_title }]}
          />
          <MediaGallery productImages={data.main.product_pictures} />
          <div className="mx-4 md:mr-20 md:ml-10">
            <div className="mb-2 text-xl font-medium text-red md:mb-4 md:text-3xl">
              {data.main.product_name}
            </div>
            <p className="mb-2 md:mb-4 md:leading-6">{data.main.product_description}</p>
            {selectedItem && (
              <div>
                {selectedItem.description && (
                  <p className="mb-2 font-bold md:mb-4 md:leading-6">{selectedItem.description}</p>
                )}
                {selectedItem.item_data.length > 0 && (
                  <table className="mb-6 text-xs md:mb-8 md:text-base">
                    {selectedItem.item_data.map((data) => (
                      <tr key={data.label}>
                        <td className="font-bold">{data.label}:</td>
                        <td className="pl-2">{data.value}</td>
                      </tr>
                    ))}
                  </table>
                )}
              </div>
            )}

            {data.main.items.length > 1 && (
              <form onSubmit={handleSubmit(onSubmit)}>
                {data.main.options.map((option) => (
                  <Options key={`${option.title}-${option.uid}`} {...option} />
                ))}
                <button type="submit" className="sr-only">
                  submit
                </button>
                <DevTool control={control} />
              </form>
            )}
          </div>
        </div>
        {data.main.product_information && renderModules(data.main.product_information)}
      </div>
      {data.main.content_modules &&
        data.main.content_modules.map((mod, i) => selectContentModule(mod, i))}
    </div>
  )
}
